import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, useMediaQuery } from '@mui/material';
import { Page, FullTable as Table, MobileTable } from '../../components';
import { IRepresentedByAPI } from '../../fetch/represented-by';
import { useToastContext } from '../../hooks';
import { IRepresentedBy, IRepresentedByRes } from '../../models';
import { Edit, DeleteOutlined } from '@mui/icons-material';
import { AddEditRepresentedByModal } from './AddEditRepresentedByModal';

export const RepresentedBy: FC = () => {
  const { showToast } = useToastContext();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [loadingRepresentedByList, setLoadingRepresentedByList] = useState(false);
  const [representedByList, setRepresentedByList] = useState<IRepresentedBy[]>([]);
  const [page, setPage] = useState(0);
  const [perPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);
  const [selectedSort, setSelectedSort] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<{ [key: string]: 'Asc' | 'Desc' }>({});
  const [editingRepresentedBy, setEditingRepresentedBy] = useState<IRepresentedBy | undefined>(undefined);

  const fetchRepresentedByList = async () => {
    setLoadingRepresentedByList(true);
    try {
      const sortParams = selectedSort
        ? { sortBy: selectedSort, sortDirection: sortDirection[selectedSort] }
        : {};
      const representedByListRes: IRepresentedByRes = await IRepresentedByAPI.getAll({
        page: page + 1,
        perPage,
        ...sortParams,
      });

      setRepresentedByList(representedByListRes.records);
      setRecordCount(representedByListRes.totalRecordCount);

    } catch (error: any) {
      showToast('error', error?.detail || 'Error retrieving represented by list.');
    } finally {
      setLoadingRepresentedByList(false);
    }
  };

  useEffect(() => {
    fetchRepresentedByList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRepresentedByList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, selectedSort, sortDirection]);

  const handleClickColumn = (column: string) => {
    // If no sort exists yet for this column, default to Asc.
    const currentDirection = sortDirection[column] || 'Asc';
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: currentDirection === 'Asc' ? 'Desc' : 'Asc',
    });
  };

  // Define table columns
  const columns = useMemo(() => {
    return [
      {
        accessor: 'role',
        Header: 'Role',
        isServerSorted: selectedSort === 'role',
        isServerSortedDesc: sortDirection.productType === 'Desc',
        handleClickColumn: () => handleClickColumn('role'),
      },
      {
        accessor: 'actions',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: any) => (
          <Box display="flex" justifyContent="flex-end">
            <IconButton onClick={() => setEditingRepresentedBy(original)}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => handleDeleteRepresentedBy(original)} color="error">
              <DeleteOutlined />
            </IconButton>
          </Box>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ representedByList,selectedSort, sortDirection]);

    const handleDeleteRepresentedBy = async (representedByToDelete: IRepresentedBy) => {

        const result = window.confirm('Are you sure you want to remove this represented by?');
        if (!result) return;
        try {
      
          await IRepresentedByAPI.delete(representedByToDelete.representedById);
          showToast('success', 'Represented By record deleted successfully.');
          fetchRepresentedByList();
        } catch (error: any) {
          showToast('error', error?.detail || 'Error deleting Represented By record.');
            }

  };

  const handleSaveRepresentedBy = async (updatedRepresentedBy: IRepresentedBy) => {
    
    try {
        console.log('handleSaveRepresentedBy', updatedRepresentedBy);
      await IRepresentedByAPI.upsert(updatedRepresentedBy);
      showToast('success', updatedRepresentedBy.representedById ? 'Represented By record updated.' : 'Represented By record created.');
      fetchRepresentedByList();

    } catch (error: any) {
      showToast('error', error?.detail || 'Error saving Represented By.');
    } finally {
      setEditingRepresentedBy(undefined);
    }

  };

  return (
    <Page title="Represented By">
      <Box>
        <Box display="flex" justifyContent="flex-end" mb={0.25}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setEditingRepresentedBy(IRepresentedBy.getDefault())}
          >
            Add Represented By
          </Button>
        </Box>
        <Table
          columns={columns}
          data={representedByList}
          isLoading={loadingRepresentedByList}
          stickyHeader
          serverPage={page}
          serverRecordCount={recordCount}
          serverPerPage={perPage}
          handlePage={setPage}
          handleRowsPerPage={setRowsPerPage}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
          mobileProps={{
            fields: columns
              .filter(col => col.Header)
              .map(col => ({
                name: col.Header,
                accessor: col.accessor,
                Cell: col?.Cell,
              })),
              mobileAccordionLabel: (original: any) => original.role,
            handleEdit: (original: any) => setEditingRepresentedBy(original),
          }}
        />
      </Box>
      <AddEditRepresentedByModal
        open={!!editingRepresentedBy}
        representedBy={editingRepresentedBy}
        onClose={() => setEditingRepresentedBy(undefined)}
        onSave={handleSaveRepresentedBy}
      />
    </Page>
  );
};
